import { useEffect } from "react"
import { useLocation } from "react-router-dom"

/**
 * A custom React hook to track page views for Google Tag Manager (GTM)
 * on client-side route changes in a Single-Page Application.
 *
 * This hook waits for route changes and ensures that a page view event is pushed.
 * It optionally checks if GTM is already loaded (by inspecting window.dataLayer)
 * and will only push the event if GTM appears to be present.
 *
 * Usage:
 *    Place usePageViewTracker() high up (e.g., in your App component).
 */
export const usePageViewTracker = (): void => {
  const location = useLocation()

  useEffect(() => {
    const timer = setTimeout(() => {
      // If the dataLayer is not defined, you might consider triggering the GTM loading function.
      // In our case, we assume that the server-side injection ensures GTM is already present.
      if (!window.dataLayer) {
        // Optionally, you could call a function to load GTM here if needed:
        // loadGoogleAnalytics();
      }
      // Push the page view event once GTM is ready.
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({
        event: "page_view",
        page: location.pathname + location.search,
      })
    }, 1000) // Delay in ms—adjust based on how long GTM takes to load.

    return () => clearTimeout(timer)
  }, [location])
}
